import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Breadcrumb from '@/components/common/Breadcrumb';
import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import CardBody from '@/components/ui/cards/CardBody';
import { Input } from '@/components/ui/inputs/input';
import { useDispatch } from 'react-redux';
import validationErrors from '@/lib/validationErrors';
import { getCustomerProfileService, updateCustomerProfileService } from '@/services/CustomerServices';

const CustomerProfilePage = ({ pageTitle = 'Edit Profile' }) => {
  const dispatch = useDispatch();

  const {
    control,
    handleSubmit,
    reset,                                     // ⬅ use reset
    formState: { errors },
  } = useForm({
    defaultValues: {                           // ⬅ keep controlled from first render
      name: '',
      email: '',
      phone_no: '',
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    dispatch(getCustomerProfileService())
      .then((res) => {
        const d = res?.data?.data || {};
        reset({
          name: d.name || '',
          email: d.email || '',
          phone_no: d.phone_no || '',
        });
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, reset]);

  const onSubmit = (formData) => {
    setIsSubmitting(true);
    dispatch(updateCustomerProfileService(formData))
      .then(() => dispatch(getCustomerProfileService()))
      .then((res) => {
        const d = res?.data?.data || {};
        reset({
          name: d.name || '',
          email: d.email || '',
          phone_no: d.phone_no || '',
        });
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <>
      <Breadcrumb pageTitle={pageTitle} />
      <Card>
        <CardHeader isBorder>
          <h3 className="db-card-title">{pageTitle}</h3>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Controller
                name="name"
                control={control}
                rules={{ required: true, minLength: 2, maxLength: 50 }}
                render={({ field }) => (
                  <Input
                    {...field}
                    labelText="Name"
                    labelStyleClassName="db-field-title required"
                    size="sm"
                    type="text"
                    errorType={errors.name?.type}
                    errorData={{ minLength: '2', maxLength: '50' }}
                  />
                )}
              />

              <Controller
                name="email"
                control={control}
                rules={{ required: true, pattern: /^[^@ ]+@[^@ ]+\.[^@ ]{2,}$/ }}
                render={({ field }) => (
                  <Input
                    {...field}
                    labelText="Email"
                    labelStyleClassName="db-field-title required"
                    size="sm"
                    type="email"
                    errorType={errors.email?.type}
                    errorData={{ pattern: 'Invalid email format' }}
                  />
                )}
              />

              <Controller
                name="phone_no"
                control={control}
                rules={{ required: true, minLength: 7, maxLength: 20 }}
                render={({ field }) => (
                  <Input
                    {...field}
                    labelText="Phone"
                    labelStyleClassName="db-field-title required"
                    size="sm"
                    type="text"
                    errorType={errors.phone_no?.type}
                    errorData={{ minLength: '7', maxLength: '20' }}
                  />
                )}
              />

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  className="db-btn bg-primary text-white"
                  disabled={isLoading || isSubmitting}
                >
                  <i className="lab lab-save" />
                  <span>Save</span>
                  {isSubmitting && (
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </form>
        </CardBody>
      </Card>
    </>
  );
};

export default CustomerProfilePage;
